<template>
  <v-layout column>
    <vbt-search
      v-model="searchData"
      :items="searchFieldsConfig"
      @reset="onResetSearch"
      @search="onSearch"
    />

    <vbt-content-box
      :loading="isLoading"
      :loading-error="loadingError"
      :no-content="noContent"
      :go-back-btn="goBack"
      title="Information"
    >
      <vbt-table
        :items="orders"
        :headers="headers"
        show-details-action
        @click:details="({ outerId: id }) => $router.push({
          name: 'order',
          params: { id },
          query: { suffix },
        })"
      >
        <template #outerId="{ item: { outerId } }">
          <router-link
            :to="{
              name: 'order',
              params: { id: outerId },
              query: { suffix },
            }"
            class="info--text"
          >
            {{ outerId }}
          </router-link>
        </template>
        <template #status="{ item: { status } }">
          <kernel-status-badge :status="orderStatusEnumByValue[status]">
            {{ status }}
          </kernel-status-badge>
        </template>
        <template #validationStatus="{ item: { validationStatus } }">
          <kernel-status-badge :status="validationStatusEnumByValue[validationStatus]">
            {{ validationStatus }}
          </kernel-status-badge>
        </template>
        <template #created="{ item: { created } }">
          {{ formatDate(created) }}
        </template>
        <template #expires="{ item: { expires } }">
          {{ formatDate(expires) }}
        </template>
      </vbt-table>

      <vbt-pagination
        v-model="pageNum"
        :items-count="pagedItemsCount"
      />
    </vbt-content-box>
  </v-layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import { useDataLoading, usePagination, useSearch } from '@mixins/factories';
import { formatDate } from '@helpers';

import { namespace as EnumsNamespace } from '@store/modules/enums.types';
import { namespace as LocationsNamespace } from '@store/modules/locations.types';

import { namespace as OrdersNamespace, ActionTypes as OrdersActionTypes } from '../store.types';

const dataLoadingOptions = { getterName: OrdersActionTypes.GET_ORDERS, contentName: 'orders' };
const paginationOptions = { contentName: 'orders' };
const searchOptions = {
  initialSearchDataStates: [
    'state', 'validationState', 'outerId', 'customerId', 'printCustomer',
  ],
};

const headers = Object.freeze([
  { text: 'Outer ID', value: 'outerId' },
  { text: 'Status', value: 'status' },
  { text: 'Validation Status', value: 'validationStatus' },
  { text: 'Created Date', value: 'created' },
  { text: 'Expires Date', value: 'expires' },
  { text: 'Print Customer', value: 'printCustomer' },
]);

export default {
  name: 'KernelOrders',
  mixins: [
    useDataLoading(dataLoadingOptions),
    usePagination(paginationOptions),
    useSearch(searchOptions),
  ],
  data: () => ({ headers }),
  computed: {
    ...mapState(OrdersNamespace, ['orders']),
    ...mapState(LocationsNamespace, ['suffix']),
    ...mapState(EnumsNamespace, {
      orderActivityStatuses: ({ enums: { orderStatus: enums } }) => (enums || [])
        .map(({ value }) => value),
      validationStatuses: ({ enums: { validationStatus: enums } }) => (enums || [])
        .map(({ value }) => value),
    }),
    ...mapGetters(EnumsNamespace, ['orderStatusEnumByValue', 'validationStatusEnumByValue']),
    searchFieldsConfig() {
      return [
        {
          flex: 'lg4',
          value: 'outerId',
          label: 'Outer ID',
          type: 'text',
        },
        {
          flex: 'lg4',
          value: 'customerId',
          label: 'Customer ID',
          type: 'text',
        },
        {
          flex: 'lg4',
          value: 'printCustomer',
          label: 'Print Customer',
          type: 'text',
        },
        {
          flex: 'lg4',
          value: 'state',
          label: 'State',
          type: 'select',
          items: this.orderActivityStatuses,
        },
        {
          flex: 'lg4',
          value: 'validationState',
          label: 'Validation State',
          type: 'select',
          items: this.validationStatuses,
        },
      ];
    },
  },
  watch: {
    suffix: '$_refreshData',
  },
  methods: {
    ...mapActions(OrdersNamespace, [OrdersActionTypes.GET_ORDERS]),
    formatDate,
  },
};
</script>
